/**
 * PresenceActivation — single Reclaim Presence Activation player.
 *
 * Route: /reclaim-presence/activation/:slug
 *
 * Behaviour:
 *  - Loads card metadata from /api/presence/activations/:slug
 *  - If `audio_url` is present, renders a big play button + native
 *    <audio> tag. Works for both self-rendered (Object Storage) and
 *    external/linked cards (Morning Light → Sleep Installations,
 *    Wake Up → Morning Activation Express).
 *  - If `ready` is false, shows the script placeholder + "coming soon".
 *  - Smart back via smartBack util.
 */
import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Play, Pause, Loader2, ExternalLink, Download } from "lucide-react";
import { smartBack } from "../../utils/smartBack";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PresenceActivation() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/api/presence/activations/${slug}`, { cache: "no-store" });
        if (r.ok && !cancelled) setCard(await r.json());
      } catch (_) { /* show error UI */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause();
    else audioRef.current.play().catch(() => {});
  };

  const handleBack = () => smartBack(navigate, "/reclaim-presence/activations");

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A09] flex items-center justify-center">
        <Loader2 size={24} className="text-emerald-500 animate-spin" />
      </div>
    );
  }

  if (!card) {
    return (
      <div className="min-h-screen bg-[#0A0A09] text-[#9A9A98] flex flex-col items-center justify-center px-6 gap-3">
        <p>Activation not found.</p>
        <button onClick={handleBack} className="text-emerald-500 underline">Back</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A09]" data-testid="presence-activation-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#0A0A09]/90 backdrop-blur-lg border-b border-[#1A1A18]">
        <div className="px-5 py-4 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="p-2 -ml-2 rounded-lg hover:bg-[#1A1A18]"
            data-testid="presence-activation-back"
          >
            <ArrowLeft size={20} className="text-[#9A9A98]" />
          </button>
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-500/70">
              Daily Awareness
            </p>
            <p className="text-[#F2EFE9] text-sm font-medium mt-0.5">{card.category}</p>
          </div>
          <div className="w-8" />
        </div>
      </header>

      <main className="px-6 pt-10 pb-24 max-w-md mx-auto text-center">
        <h1 className="font-['Playfair_Display'] text-3xl text-[#F2EFE9] leading-tight">
          {card.title}
        </h1>
        <p className="text-[#8A8A88] text-base mt-3 leading-relaxed">
          {card.subtitle}
        </p>
        {card.narrator_byline && (
          <p
            className="mt-4 text-sm italic"
            style={{ color: "#C9A46A", fontFamily: '"Cormorant Garamond", serif', letterSpacing: "0.02em" }}
            data-testid="presence-activation-narrator-byline"
          >
            {card.narrator_byline}
          </p>
        )}

        {/* Audio player or "coming soon" */}
        <div className="mt-12">
          {card.ready && card.audio_url ? (
            <>
              <button
                onClick={toggle}
                className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-600 to-teal-700 flex items-center justify-center mx-auto shadow-lg shadow-emerald-900/40 hover:scale-105 transition-transform"
                data-testid="presence-activation-play-btn"
              >
                {playing
                  ? <Pause size={36} className="text-white" />
                  : <Play size={36} className="text-white ml-1" fill="white" />}
              </button>
              <p className="text-xs text-[#6A6A68] mt-4">
                {card.duration_label}{card.duration_s ? ` · ${Math.round(card.duration_s)}s rendered` : ""}
              </p>

              <audio
                ref={audioRef}
                src={`${API}${card.audio_url}`}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                preload="metadata"
                data-testid="presence-activation-audio-el"
              />

              {/* Native controls fallback (helps if the big button fails on iOS autoplay block) */}
              <audio
                src={`${API}${card.audio_url}`}
                controls
                className="w-full mt-6 opacity-80"
                data-testid="presence-activation-native-audio"
              />

              {/* Download link for offline listening (matches Sam-style share UX) */}
              {!card.is_external && (
                <a
                  href={`${API}${card.audio_url}?download=1`}
                  className="inline-flex items-center gap-2 mt-6 text-emerald-500/80 hover:text-emerald-400 text-sm"
                  data-testid="presence-activation-download"
                >
                  <Download size={14} /> Download to your phone
                </a>
              )}

              {card.summary && (
                <p className="text-xs text-[#6A6A68] mt-8 italic">{card.summary}</p>
              )}

              {card.is_external && card.external_link && (
                <Link
                  to={card.external_link}
                  className="inline-flex items-center gap-2 mt-3 text-emerald-500/80 hover:text-emerald-400 text-sm"
                  data-testid="presence-activation-external-link"
                >
                  Open in its primary home <ExternalLink size={12} />
                </Link>
              )}
            </>
          ) : (
            <div className="rounded-2xl border border-[#1F1F1B] bg-[#0F0F0D] px-6 py-10">
              <p className="text-[#9A9A98] text-sm leading-relaxed">
                Michael is recording this one in V3 voice over a soft healing music bed.
              </p>
              <p className="text-emerald-500/70 text-xs mt-3 uppercase tracking-wider">
                Coming soon
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
